// Client-side media handling.
//
// ADR-0004: images can be converted here (canvas is cheap and universal);
// video cannot, because that needs FFmpeg and neither runtime has it. This
// file therefore reads facts about video and rewrites images.

export interface MediaFacts {
  mimeType: string;
  byteSize: number;
  width: number | null;
  height: number | null;
  durationSeconds: number | null;
}

export async function inspect(file: File): Promise<MediaFacts> {
  const base: MediaFacts = {
    mimeType: file.type || "application/octet-stream",
    byteSize: file.size,
    width: null,
    height: null,
    durationSeconds: null,
  };

  if (file.type.startsWith("image/")) {
    const bitmap = await createImageBitmap(file).catch(() => null);
    if (bitmap) {
      base.width = bitmap.width;
      base.height = bitmap.height;
      bitmap.close();
    }
    return base;
  }

  if (file.type.startsWith("video/")) {
    const facts = await readVideoFacts(file).catch(() => null);
    if (facts) Object.assign(base, facts);
  }
  return base;
}

function readVideoFacts(file: File): Promise<Partial<MediaFacts>> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: video.videoWidth || null,
        height: video.videoHeight || null,
        // Infinity happens on some streamed containers; report unknown rather
        // than a wrong number, so the validator can warn instead of pass.
        durationSeconds: Number.isFinite(video.duration) ? video.duration : null,
      });
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read video metadata"));
    };
    video.src = url;
  });
}

export interface ConvertOptions {
  maxWidth?: number;      // Instagram caps at 1440 [C011]
  minWidth?: number;      // and requires at least 320 [C011]
  maxBytes?: number;      // 8 MB [C009]
  minAspect?: number;     // 4:5 [C010]
  maxAspect?: number;     // 1.91:1 [C010]
}

/**
 * Re-encode an image to JPEG within the given constraints.
 *
 * Aspect ratio is corrected by centre-cropping to the nearest allowed ratio —
 * the crop is visible in the preview before publishing, never applied silently.
 */
export async function convertImage(file: File, opts: ConvertOptions): Promise<File> {
  const bitmap = await createImageBitmap(file);

  let sx = 0, sy = 0, sw = bitmap.width, sh = bitmap.height;
  const ar = sw / sh;

  if (opts.minAspect && ar < opts.minAspect) {
    sh = Math.round(sw / opts.minAspect);          // too tall: trim height
    sy = Math.round((bitmap.height - sh) / 2);
  } else if (opts.maxAspect && ar > opts.maxAspect) {
    sw = Math.round(sh * opts.maxAspect);          // too wide: trim width
    sx = Math.round((bitmap.width - sw) / 2);
  }

  let targetW = sw;
  let targetH = sh;
  if (opts.maxWidth && targetW > opts.maxWidth) {
    targetH = Math.round(targetH * (opts.maxWidth / targetW));
    targetW = opts.maxWidth;
  }
  if (opts.minWidth && targetW < opts.minWidth) {
    targetH = Math.round(targetH * (opts.minWidth / targetW));
    targetW = opts.minWidth;
  }

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is unavailable in this browser");

  // JPEG has no alpha; flatten onto white so transparency does not turn black.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, targetW, targetH);
  ctx.drawImage(bitmap, sx, sy, sw, sh, 0, 0, targetW, targetH);
  bitmap.close();

  // Step quality down until it fits, rather than guessing once.
  const maxBytes = opts.maxBytes ?? Infinity;
  for (const quality of [0.92, 0.85, 0.75, 0.65, 0.5]) {
    const blob = await new Promise<Blob | null>((r) =>
      canvas.toBlob(r, "image/jpeg", quality)
    );
    if (blob && blob.size <= maxBytes) {
      return new File([blob], replaceExt(file.name, "jpg"), { type: "image/jpeg" });
    }
  }
  throw new Error("Could not compress this image below the platform limit. Try a smaller image.");
}

function replaceExt(name: string, ext: string): string {
  return name.replace(/\.[^.]+$/, "") + "." + ext;
}

/**
 * Sizes as a person would say them.
 *
 * Runs all the way to TB rather than stopping at MB: the media library is a
 * place people keep hundreds of files, and "3072.0 MB" next to a video is a
 * number nobody reads as three gigabytes.
 */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v < 10 ? v.toFixed(1) : v.toFixed(0)} ${units[i]}`;
}

/**
 * SHA-256 of the file, for duplicate detection.
 *
 * Skipped above a size threshold: hashing needs the whole file in memory, and
 * a 300 MB reel [C014] is not worth an out-of-memory error on a low-end phone
 * to spot a duplicate. Null means "not computed", not "unique".
 */
const CHECKSUM_MAX_BYTES = 50 * 1024 * 1024;

export async function checksum(file: File): Promise<string | null> {
  if (file.size > CHECKSUM_MAX_BYTES) return null;
  try {
    const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ folders
 *
 * Uploading a folder should keep its shape. Browsers expose that in two
 * unrelated ways and neither is `File[]`:
 *
 *   - dropped items carry a non-standard but universally implemented
 *     `webkitGetAsEntry()`, which is the only way to see a *dropped* directory;
 *   - `<input webkitdirectory>` gives plain Files with a `webkitRelativePath`.
 *
 * Both are normalised to the same thing: the file, plus the folder names above
 * it. What those names become in the library is the caller's business.
 */

export interface DroppedFile {
  file: File;
  /** Folder names from the top down; empty for a file dropped on its own. */
  path: string[];
}

const isMedia = (f: File) =>
  f.type.startsWith("image/") || f.type.startsWith("video/");

/** Directories a camera, a phone or an OS leaves behind and nobody filed there. */
const IGNORED = new Set([".DS_Store", "Thumbs.db", "desktop.ini", "__MACOSX"]);

/**
 * Files from a drop, with their folder structure.
 *
 * The entries must be taken from the DataTransfer **synchronously** — the list
 * is emptied when the drop handler returns, so awaiting first yields nothing.
 * That is why this takes entries rather than the event.
 */
export function entriesFromDrop(dt: DataTransfer): any[] {
  return Array.from(dt.items)
    .filter((i) => i.kind === "file")
    .map((i) => (i as any).webkitGetAsEntry?.() ?? null)
    .filter(Boolean);
}

export async function filesFromEntries(entries: any[]): Promise<DroppedFile[]> {
  const out: DroppedFile[] = [];
  await Promise.all(entries.map((entry) => walkEntry(entry, [], out)));
  return out;
}

async function walkEntry(entry: any, path: string[], out: DroppedFile[]): Promise<void> {
  if (!entry || IGNORED.has(entry.name)) return;

  if (entry.isFile) {
    const file = await new Promise<File | null>((resolve) =>
      entry.file((f: File) => resolve(f), () => resolve(null)));
    if (file && isMedia(file)) out.push({ file, path });
    return;
  }

  if (entry.isDirectory) {
    const reader = entry.createReader();
    // readEntries hands back at most 100 at a time and signals the end with an
    // empty batch. Calling it once reads the first hundred files of a shoot and
    // silently loses the rest — a bug that only shows up on a real library.
    for (;;) {
      const batch: any[] = await new Promise((resolve) =>
        reader.readEntries((e: any[]) => resolve(e), () => resolve([])));
      if (batch.length === 0) break;
      for (const child of batch) await walkEntry(child, [...path, entry.name], out);
    }
  }
}

/**
 * The same shape from `<input webkitdirectory>`. `webkitRelativePath` is
 * "Clients/BluePeak/aug/reel.mp4"; the last segment is the file itself, and the
 * first is the folder the person actually chose.
 */
export function filesFromInput(files: File[]): DroppedFile[] {
  return files.filter(isMedia).map((file) => {
    const relative = (file as any).webkitRelativePath as string | undefined;
    const parts = relative ? relative.split("/").filter(Boolean) : [];
    return { file, path: parts.slice(0, -1).filter((p) => !IGNORED.has(p)) };
  });
}

/** Folder names the database will accept: no slashes, trimmed, 80 characters. */
export function safeFolderName(raw: string): string {
  const cleaned = raw.replace(/[/\\]/g, " ").trim().slice(0, 80).trim();
  return cleaned || "Untitled folder";
}

// --------------------------------------------------------- derivatives (0017-v2 §2)
//
// A small copy made here, on the device that already holds the file, so that a
// grid of two-hundred-pixel squares stops downloading four-megabyte originals.
// ADR-0004 is untouched: this is the browser re-encoding an image it already
// decoded, not a server transcoding pipeline.

/** Longest edge of a derivative. Covers every grid, picker and calendar tile at 2x. */
export const PREVIEW_MAX_EDGE = 480;

export interface Derivative {
  file: File;
  width: number;
  height: number;
}

/**
 * A preview for anything we can draw — a photo, or a video's first usable frame.
 *
 * Returns null rather than throwing. A derivative is an optimisation, and an
 * upload that fails because a thumbnail could not be encoded would be a worse
 * product than one that falls back to serving the original.
 */
export async function makePreview(file: File): Promise<Derivative | null> {
  try {
    if (file.type.startsWith("image/")) return await previewFromImage(file);
    if (file.type.startsWith("video/")) return await previewFromVideo(file);
  } catch {
    // Deliberately silent: see above.
  }
  return null;
}

async function previewFromImage(file: File): Promise<Derivative | null> {
  const bitmap = await createImageBitmap(file);
  try {
    return await drawPreview(bitmap, bitmap.width, bitmap.height, file.name);
  } finally {
    bitmap.close();
  }
}

/**
 * A poster frame, grabbed from a video the browser has locally.
 *
 * The comment this replaces said a poster would mean downloading the file, and
 * that is true of a *library* asset and false of an *uploading* one — the bytes
 * are on this machine already. Seeking a little way in avoids the black or
 * blank first frame most cameras produce.
 */
function previewFromVideo(file: File): Promise<Derivative | null> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    let settled = false;

    const done = (value: Derivative | null) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      video.removeAttribute("src");
      resolve(value);
    };

    // Some containers never fire `seeked`, and a hung promise here would hang
    // the upload behind it. Ten seconds is far longer than a local seek needs.
    const timer = setTimeout(() => done(null), 10_000);

    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = () => {
      // A tenth of the way in, capped at one second: far enough past the fade
      // most clips open on, near enough that a long video is not a long seek.
      video.currentTime = Math.min(1, (video.duration || 0) / 10) || 0;
    };
    video.onseeked = () => {
      void (async () => {
        const frame = await drawPreview(video, video.videoWidth, video.videoHeight, file.name)
          .catch(() => null);
        clearTimeout(timer);
        done(frame);
      })();
    };
    video.onerror = () => { clearTimeout(timer); done(null); };
    video.src = url;
  });
}

/** Scale to fit `PREVIEW_MAX_EDGE`, encode, and never upscale. */
async function drawPreview(
  source: CanvasImageSource,
  width: number,
  height: number,
  name: string,
): Promise<Derivative | null> {
  if (!width || !height) return null;

  const scale = Math.min(1, PREVIEW_MAX_EDGE / Math.max(width, height));
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  // A preview is opaque: flattening onto white keeps a transparent PNG from
  // going black, the same reason `convertImage` does it.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(source, 0, 0, w, h);

  // WebP first, because it is roughly a third of the JPEG at this size. Every
  // browser that supports it returns a WebP blob; one that does not returns
  // whatever it can, so the type is read back off the blob rather than assumed.
  const blob = await encode(canvas, "image/webp", 0.8)
    ?? await encode(canvas, "image/jpeg", 0.8);
  if (!blob) return null;

  const ext = blob.type === "image/webp" ? "webp" : "jpg";
  return {
    file: new File([blob], replaceExt(name, ext), { type: blob.type }),
    width: w,
    height: h,
  };
}

function encode(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) =>
    // `toBlob` hands back a PNG when it does not know the type, so a browser
    // without WebP would otherwise "succeed" with a file larger than the JPEG.
    canvas.toBlob((b) => resolve(b && b.type === type ? b : null), type, quality),
  );
}
